import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  InputAdornment,
  Alert,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Product, CreateProductData, UpdateProductData } from '../../services/productService';

interface ProductFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateProductData | UpdateProductData) => Promise<void>;
  product?: Product | null;
  loading?: boolean;
}

const CATEGORIES = [
  'Makeup',
  'Hair Care',
  'Skincare',
  'Nail Care',
  'Spa & Wellness',
  'Tools & Equipment',
  'Accessories',
  'Cleaning Supplies',
];

const BRANDS = [
  'MAC',
  'Loreal',
  'Maybelline',
  'Lakme',
  'Revlon',
  'Cetaphil',
  'Neutrogena',
  'OPI',
  'Essie',
  'Schwarzkopf',
  'Matrix',
  'Wella',
  'Other',
];

const UNITS = [
  'pieces',
  'ml',
  'grams',
  'liters',
  'kg',
  'bottles',
  'tubes',
  'packets',
];

const ProductForm: React.FC<ProductFormProps> = ({
  open,
  onClose,
  onSubmit,
  product,
  loading = false,
}) => {
  const [formData, setFormData] = useState<CreateProductData>({
    name: '',
    brand: '',
    category: '',
    description: '',
    costPrice: 0,
    sellingPrice: 0,
    currentStock: 0,
    minStockLevel: 0,
    maxStockLevel: 0,
    unit: 'pieces',
    expiryDate: '',
    supplierId: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name,
        brand: product.brand,
        category: product.category,
        description: product.description || '',
        costPrice: product.costPrice,
        sellingPrice: product.sellingPrice,
        currentStock: product.currentStock,
        minStockLevel: product.minStockLevel,
        maxStockLevel: product.maxStockLevel,
        unit: product.unit,
        expiryDate: product.expiryDate ? product.expiryDate.split('T')[0] : '',
        supplierId: product.supplierId || '',
      });
    } else {
      // Reset form for new product
      setFormData({
        name: '',
        brand: '',
        category: '',
        description: '',
        costPrice: 0,
        sellingPrice: 0,
        currentStock: 0,
        minStockLevel: 0,
        maxStockLevel: 0,
        unit: 'pieces',
        expiryDate: '',
        supplierId: '',
      });
    }
    setErrors({});
  }, [product, open]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = 'Product name is required';
    if (!formData.brand.trim()) newErrors.brand = 'Brand is required';
    if (!formData.category.trim()) newErrors.category = 'Category is required';
    if (!formData.unit.trim()) newErrors.unit = 'Unit is required';
    
    if (formData.costPrice <= 0) newErrors.costPrice = 'Cost price must be greater than 0';
    if (formData.sellingPrice <= 0) newErrors.sellingPrice = 'Selling price must be greater than 0';
    if (formData.currentStock < 0) newErrors.currentStock = 'Current stock cannot be negative';
    if (formData.minStockLevel < 0) newErrors.minStockLevel = 'Minimum stock level cannot be negative';
    if (formData.maxStockLevel < 0) newErrors.maxStockLevel = 'Maximum stock level cannot be negative';
    
    if (formData.minStockLevel > formData.maxStockLevel) {
      newErrors.minStockLevel = 'Minimum stock cannot be greater than maximum stock';
    }
    
    if (formData.sellingPrice < formData.costPrice) {
      newErrors.sellingPrice = 'Selling price should be greater than cost price';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const submitData = {
        ...formData,
        expiryDate: formData.expiryDate || undefined,
        supplierId: formData.supplierId || undefined,
      };
      
      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Error submitting product form:', error);
    }
  };

  const calculateProfitMargin = () => {
    if (formData.costPrice > 0 && formData.sellingPrice > 0) {
      return (((formData.sellingPrice - formData.costPrice) / formData.costPrice) * 100).toFixed(1);
    }
    return '0';
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
          },
        }}
      >
        <DialogTitle 
          sx={{ 
            background: 'linear-gradient(135deg, #E8B4B8 0%, #F5D5D8 100%)',
            color: 'white',
            fontWeight: 800,
            fontSize: '1.5rem',
            textAlign: 'center',
            py: 3,
          }}
        >
          {product ? 'Edit Product' : 'Add New Product'}
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  bgcolor: 'rgba(232, 180, 184, 0.05)',
                  borderRadius: 3,
                  border: '1px solid rgba(232, 180, 184, 0.1)',
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#E8B4B8' }}>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Product Name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      error={!!errors.name}
                      helperText={errors.name}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#E8B4B8' },
                          '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={!!errors.brand}>
                      <InputLabel>Brand</InputLabel>
                      <Select
                        value={formData.brand}
                        onChange={(e) => setFormData(prev => ({ ...prev, brand: e.target.value }))}
                        label="Brand"
                        sx={{
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                        }}
                      >
                        {BRANDS.map((brand) => (
                          <MenuItem key={brand} value={brand}>{brand}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={!!errors.category}>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={formData.category}
                        onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                        label="Category"
                        sx={{
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                        }}
                      >
                        {CATEGORIES.map((category) => (
                          <MenuItem key={category} value={category}>{category}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={!!errors.unit}>
                      <InputLabel>Unit</InputLabel>
                      <Select
                        value={formData.unit}
                        onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                        label="Unit"
                        sx={{
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                        }}
                      >
                        {UNITS.map((unit) => (
                          <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      multiline
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#E8B4B8' },
                          '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Pricing Information */}
            <Grid item xs={12}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  bgcolor: 'rgba(232, 180, 184, 0.05)',
                  borderRadius: 3,
                  border: '1px solid rgba(232, 180, 184, 0.1)',
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#E8B4B8' }}>
                  Pricing Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Cost Price"
                      type="number"
                      value={formData.costPrice}
                      onChange={(e) => setFormData(prev => ({ ...prev, costPrice: Number(e.target.value) }))}
                      error={!!errors.costPrice}
                      helperText={errors.costPrice}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#E8B4B8' },
                          '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Selling Price"
                      type="number"
                      value={formData.sellingPrice}
                      onChange={(e) => setFormData(prev => ({ ...prev, sellingPrice: Number(e.target.value) }))}
                      error={!!errors.sellingPrice}
                      helperText={errors.sellingPrice}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#E8B4B8' },
                          '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Alert 
                      severity="info" 
                      sx={{ 
                        borderRadius: 2,
                        bgcolor: 'rgba(6, 182, 212, 0.1)',
                        border: '1px solid rgba(6, 182, 212, 0.2)',
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Profit Margin:</strong> {calculateProfitMargin()}%
                        {formData.sellingPrice < formData.costPrice && (
                          <span style={{ color: '#EF4444', marginLeft: 8 }}>
                            (Loss: ₹{(formData.costPrice - formData.sellingPrice).toFixed(2)})
                          </span>
                        )}
                      </Typography>
                    </Alert>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Stock Information */}
            <Grid item xs={12}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  bgcolor: 'rgba(232, 180, 184, 0.05)',
                  borderRadius: 3,
                  border: '1px solid rgba(232, 180, 184, 0.1)',
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#E8B4B8' }}>
                  Stock Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Current Stock"
                      type="number"
                      value={formData.currentStock}
                      onChange={(e) => setFormData(prev => ({ ...prev, currentStock: Number(e.target.value) }))}
                      error={!!errors.currentStock}
                      helperText={errors.currentStock}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#E8B4B8' },
                          '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Minimum Stock Level"
                      type="number"
                      value={formData.minStockLevel}
                      onChange={(e) => setFormData(prev => ({ ...prev, minStockLevel: Number(e.target.value) }))}
                      error={!!errors.minStockLevel}
                      helperText={errors.minStockLevel}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#E8B4B8' },
                          '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Maximum Stock Level"
                      type="number"
                      value={formData.maxStockLevel}
                      onChange={(e) => setFormData(prev => ({ ...prev, maxStockLevel: Number(e.target.value) }))}
                      error={!!errors.maxStockLevel}
                      helperText={errors.maxStockLevel}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#E8B4B8' },
                          '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <DatePicker
                      label="Expiry Date (Optional)"
                      value={formData.expiryDate ? dayjs(formData.expiryDate) : null}
                      onChange={(date) => setFormData(prev => ({ 
                        ...prev, 
                        expiryDate: date ? date.format('YYYY-MM-DD') : '' 
                      }))}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover fieldset': { borderColor: '#E8B4B8' },
                              '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                            },
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={onClose}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 3,
              fontWeight: 600,
              color: '#666',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 3,
              fontWeight: 700,
              bgcolor: '#E8B4B8',
              '&:hover': {
                bgcolor: '#D4A4A8',
              },
            }}
          >
            {loading ? 'Saving...' : product ? 'Update Product' : 'Add Product'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ProductForm;